import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Helpers } from '@shared/helpers/helpers';
import { Observable, of, tap } from 'rxjs';
import { LoginData } from '@shared/models/login-data.interface';
import { GlobalService } from '@core/services/global.service';
import { PermissionsService } from '@core/services/permissions.service';
import { ChannelType } from '@shared/enums/brute-force.enums';
import { STORAGE_KEYS } from '@environments/storage.constants';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	constructor(private http: HttpClient, private globalService: GlobalService, private permissionsService: PermissionsService) {}

	login(loginData: LoginData): Observable<any> {
		const url = '/api/auth/login';
		return this.http.post(url, loginData, { withCredentials: true }).pipe(catchError(Helpers.handleError));
	}

	refreshToken(): Observable<any> {
		const url = '/api/auth/refresh';
		return this.http.post(url, {}, { withCredentials: true }).pipe(
			tap((resp: any) => {
				if (resp?.token) {
					GlobalService.setItemInStorage(STORAGE_KEYS.token, resp.token);
				}
			}),
			catchError(Helpers.handleError)
		);
	}

	logout(): Observable<any> {
		const url = '/api/auth/logout';
		return this.http.post(url, {}, { withCredentials: true }).pipe(
			tap(() => this.globalService.clearEverything()),
			catchError(() => {
				this.globalService.clearEverything();
				return of(true);
			})
		);
	}

	recoverPassword(recoverPasswordData: any): Observable<any> {
		const url = '/api/auth/recover';
		return this.http.post(url, recoverPasswordData).pipe(catchError(Helpers.handleError));
	}

	resetPassword(resetPasswordData: any, restoreCode: string): Observable<any> {
		let params = new HttpParams();
		params = params.append('code', restoreCode);
		const url = '/api/auth/reset-password';
		return this.http.post(url, resetPasswordData, { params }).pipe(catchError(Helpers.handleError));
	}

	activateUser(token: string): Observable<any> {
		const url = '/api/auth/verify-user';
		return this.http.put(url, { token }).pipe(catchError(Helpers.handleError));
	}

	impersonateUser(userId: number): Observable<any> {
		const url = `/api/auth/impersonate/user/${userId}`;
		return this.http.get(url).pipe(catchError(Helpers.handleError));
	}

	leaveImpersonation(): Observable<any> {
		const url = `/api/auth/impersonate/leave`;
		return this.http.get(url).pipe(catchError(Helpers.handleError));
	}

	sendUnlockRequest(email: string): Observable<any> {
        const url = `/api/blocked-users/requests`;
        const body = { email, channel: ChannelType.CORE };

        return this.http.post(url, body).pipe(catchError(Helpers.handleError));
    }

	requiresPasswordChange(): Observable<{ requiresPasswordChange: boolean }> {
		const url = `/api/auth/requires-password-change`;
		return this.http.get<{ requiresPasswordChange: boolean }>(url).pipe(catchError(Helpers.handleError));
	}

	changePassword(password: string): Observable<any> {
		const url = `/api/auth/change-password`;
		return this.http.post(url, { password }).pipe(catchError(Helpers.handleError));
	}

	checkMfaEnabled(): Observable<{ enabled: boolean }> {
		const url = `/api/auth/check-mfa-enabled`;
		return this.http.get<{ enabled: boolean }>(url).pipe(catchError(Helpers.handleError));
	}
}
